import { useEffect, useRef, forwardRef, useImperativeHandle, useMemo } from 'react';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useTheme } from '../../../theme/ThemeContext';
import { useMarketAggregates } from '../contexts/MarketAggregatesContext';
import { AVAILABLE_MAP_ZONES } from './map/constants';
import type { MarketZoneId } from '@app/common/static-data/energy-markets';

// Import utilities
import type { MapCardHandle } from './map/types';
import MapCardHeader from './map/MapCardHeader';
import MapContainer from './map/MapContainer';
import { useMapInitialization } from './map/hooks/useMapInitialization';
import { useMapSelectionEffects } from './map/hooks/useMapSelectionEffects';
import { useZoneSelection } from './map/hooks/useZoneSelection';
import { useTabManager } from './map/hooks/useTabManager';

// Add styles to make attribution text and links visible in both themes
const mapStyles = `
  .dark .mapboxgl-ctrl-attrib {
    background-color: rgba(0, 0, 0, 0.8) !important;
  }
  .dark .mapboxgl-ctrl-attrib-inner {
    color: white !important;
  }
  .dark .mapboxgl-ctrl-attrib-inner a {
    color: white !important;
  }
  .mapboxgl-ctrl-attrib {
    background-color: rgba(255, 255, 255, 0.8) !important;
  }
  .mapboxgl-ctrl-attrib-inner {
    color: #1f2937 !important;
  }
  .mapboxgl-ctrl-attrib-inner a {
    color: #1f2937 !important;
  }
`;

interface MapCardProps {
  selectedZones: MarketZoneId[];
  onZonesChange: (zones: MarketZoneId[]) => void;
}

const MapCard = forwardRef<MapCardHandle, MapCardProps>(({ selectedZones, onZonesChange }, ref) => {
  const { theme } = useTheme();
  const { state } = useMarketAggregates();

  // Create filtered zones based on API response
  const availableZones = useMemo(() => {
    // If we have available zones from API, filter by those
    if (state.availableZones.length > 0) {
      return AVAILABLE_MAP_ZONES.filter((zone) => state.availableZones.includes(zone.id));
    }

    // If API failed, return all zones
    if (!state.availableZonesLoading && state.availableZones.length === 0) {
      return AVAILABLE_MAP_ZONES;
    }

    // While loading, return undefined to delay map initialization
    return undefined;
  }, [state.availableZones, state.availableZonesLoading]);

  // Determine if zones are ready for map initialization
  const zonesReady = availableZones !== undefined;

  // Resize observer to handle card resize
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  // Handle tab management using custom hook (must come first since activeTab is used by other hooks)
  const { activeTab, createTabChangeHandler } = useTabManager();

  // Initialize map using custom hook
  const {
    mapContainerRef,
    mapRef,
    zonesLoaded,
    zonesReloading,
    zoneFeatureMapRef,
    zoneCentersRef,
    selectedZones: selectedZonesSet,
    centerOnZone,
    setHandleZoneSelect,
  } = useMapInitialization({
    theme,
    selectedZones,
    activeTab,
    availableZones,
    zonesReady,
  });

  // Create the tab change handler with dependencies
  const primarySelected = selectedZones.length > 0 ? selectedZones[0] : null;
  const handleTabChange = createTabChangeHandler(mapRef, primarySelected, centerOnZone);

  // Handle zone selection using custom hook
  const { handleClearSelections, handleZoneSelect } = useZoneSelection({
    mapRef,
    zoneFeatureMapRef,
    selectedZones: selectedZonesSet,
    zonesLoaded,
    activeTab,
    onZonesChange,
    centerOnZone,
  });

  // Set the zone select handler in the map initialization hook
  useEffect(() => {
    if (setHandleZoneSelect) {
      setHandleZoneSelect(handleZoneSelect);
    }
  }, [handleZoneSelect, setHandleZoneSelect]);

  // Handle map selection effects using custom hook
  useMapSelectionEffects({
    mapRef,
    zoneFeatureMapRef,
    zoneCentersRef,
    selectedZone: primarySelected,
    selectedZones: selectedZonesSet,
    zonesLoaded,
    theme,
    activeTab,
    centerOnZone,
    handleClearSelections,
  });

  // Set up resize observer to handle card resizing
  useEffect(() => {
    if (!mapContainerRef.current || !mapRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      // Small delay to ensure the container has updated
      setTimeout(() => {
        if (mapRef.current) {
          mapRef.current.resize();
        }
      }, 100);
    });

    resizeObserver.observe(mapContainerRef.current);
    resizeObserverRef.current = resizeObserver;

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, [mapRef.current]);

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    resize: () => {
      if (mapRef.current) {
        mapRef.current.resize();
      }
    },
    flyToLocation: (coordinates: [number, number]) => {
      if (mapRef.current) {
        mapRef.current.flyTo({
          center: coordinates,
          zoom: 3.5,
          essential: true,
        });
      }
    },
  }));

  return (
    <div
      className="h-full bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden"
      data-card-container
    >
      <style>{mapStyles}</style>
      <div className="h-full flex flex-col relative">
        {/* Header */}
        <MapCardHeader
          zonesLoaded={zonesLoaded}
          selectedZones={selectedZones}
          activeTab={activeTab}
          onClearSelections={handleClearSelections}
          onTabChange={handleTabChange}
        />

        {/* Content Area */}
        <MapContainer
          activeTab={activeTab}
          mapContainerRef={mapContainerRef}
          zonesLoaded={zonesLoaded}
          zonesReloading={zonesReloading}
          selectedZones={selectedZones}
          onZonesChange={onZonesChange}
          availableZones={availableZones}
          zonesReady={zonesReady}
        />
      </div>
    </div>
  );
});

MapCard.displayName = 'MapCard';

export default MapCard;
